import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useContext, useState } from 'react';
import { EditableInfoContainer } from '@dealstack/frontend/shared/components/EditableInfoContainer/EditableInfoContainer.tsx';
import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { LinkPersonToFundDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/FundLinkDrawer/LinkPersonToFundDrawer.tsx';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	getFundPersonDealLinks,
	getFundByDealId,
	unlinkPersonFromFund,
} from '@dealstack/frontend/shared/dataExchange/services/fund.service.ts';
import { FundPersonDealLinkTile } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/FundPersonDealLinkSection/components/FundPersonDealLinkTile.tsx';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import {
	FGAInternalRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer.tsx';
import { FundDealLinkRes } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	linkDrawerOpen: boolean;
	openLinkDrawer?: () => void;
	onCloseLinkDrawer: () => void;
}

export const FundPersonDealLinkSection = ({
	linkDrawerOpen,
	onCloseLinkDrawer,
	openLinkDrawer,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const { selectedEntityId, selectedEntityName, dealId } = useContext(CapManageControllerContext);
	const [dealToUnlink, setDealToUnlink] = useState<FundDealLinkRes | null>(null);

	const { hasPermission: hasSupportPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: defaultToEmptyString(dealId),
		permissionRole: FGAInternalRole.DEALSTACK_SUPPORT,
	});

	// Check if the current deal is a fund
	const fundQuery = useQuery({
		queryKey: ['fundByDealId', { dealId: dealId ?? '' }],
		queryFn: ({ signal }) => getFundByDealId({ dealId: dealId ?? '' }, signal),
		enabled: !!dealId && hasSupportPermission,
	});

	const fund = fundQuery.data?.payload;
	const fundId = fund?.id;

	const dealsQuery = useQuery({
		queryKey: ['fundPersonLinkedDeals', { personId: selectedEntityId ?? '' }],
		queryFn: ({ signal }) =>
			getFundPersonDealLinks({ personId: selectedEntityId ?? '' }, signal),
		enabled: !!selectedEntityId,
	});

	const dealLinks = dealsQuery.data?.flatMap((f) => f.deals) ?? [];

	const unlinkMutation = useMutation({
		mutationFn: (variables: Parameters<typeof unlinkPersonFromFund>[0]) =>
			unlinkPersonFromFund(variables),
		onSuccess: () => {
			setDealToUnlink(null);
			return dealsQuery.refetch();
		},
	});

	const handleSuccess = () => {
		void dealsQuery.refetch();
	};

	if (!fund) {
		return null;
	}

	return (
		<>
			{dealLinks.length > 0 && (
				<div data-testid={TEST_ELEMENT_IDS.FUND_PERSON_DEAL_LINK_SECTION}>
					<EditableInfoContainer
						title={tCapManage('fund_person_link.section.title')}
						isEdit={false}
						hasEditorPermission={hasSupportPermission}
						gridColumns={2}
						useGrid={true}
						childrenPadding="0.5rem"
						onAdd={openLinkDrawer}
					>
						{dealLinks.map((deal) => (
							<FundPersonDealLinkTile
								key={deal.dealId}
								deal={deal}
								personId={selectedEntityId}
								hasEditPermission={hasSupportPermission}
								onDeleteClick={(deal) => setDealToUnlink(deal)}
							/>
						))}
					</EditableInfoContainer>
				</div>
			)}

			{hasSupportPermission && selectedEntityId && (
				<LinkPersonToFundDrawer
					personId={selectedEntityId}
					personName={selectedEntityName}
					fundId={fund.id}
					fundName={fund?.deal?.name}
					opened={linkDrawerOpen}
					onClose={onCloseLinkDrawer}
					hasPermission={hasSupportPermission}
					onSuccess={handleSuccess}
				/>
			)}

			<ConfirmDeleteDrawer
				opened={!!dealToUnlink}
				onClose={() => setDealToUnlink(null)}
				title={tCapManage('fund_person_unlink.drawer.title')}
				confirmDeleteText={dealToUnlink?.dealName ?? ''}
				inputLabel={tCapManage('fund_person_unlink.drawer.inputLabel')}
				inputPlaceholder={tCapManage('fund_person_unlink.drawer.inputPlaceholder')}
				wordMismatchErrorMessage={tCapManage('fund_person_unlink.drawer.wordMismatchError')}
				deleteBtnText={tCapManage('fund_person_unlink.drawer.confirmDelete')}
				cancelBtnText={tCapManage('fund_person_unlink.drawer.cancel')}
				deleteSuccess={unlinkMutation.isSuccess}
				deleteLoading={unlinkMutation.isPending}
				handleDelete={() => {
					if (dealToUnlink && selectedEntityId && fundId) {
						unlinkMutation.mutate({
							personId: selectedEntityId,
							dealId: dealToUnlink.dealId,
							fundId,
						});
					}
				}}
			/>
		</>
	);
};
